<template>
  <div class="w3-row tmd-grapesjs-container">
    <div class="w3-col">
      <transition name="fade" mode="out-in">
        <div id="gjs" data-type="mj-container" ref="grapesjsEditorContainer" class="grapesjs-editor-container" v-show="!loading"></div>
      </transition>
      <image-map v-if="imgMapValue.uuid"
        :value="imgMapValue"
        @input="zones => changeImgMap ({ zones })"
      />
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import ImageMap from './ImageMap'
import SpinnerLoading from 'vue-simple-spinner'
import 'url-polyfill'

// Grapes JS
import 'grapesjs/dist/css/grapes.min.css'
import grapesjs from 'grapesjs'
import grapesjsMJML from 'grapesjs-mjml'
import tUIMjImageEditor from './plugins/sherpa-grapesjs-plugin-tui-mj-image-editor'
import imageMapPlugin from './plugins/sherpa-grapesjs-plugin-mj-image-map'
import exportPlugin from './plugins/sherpa-grapesjs-plugin-mj-export'
import varsPlugin from './plugins/sherpa-grapesjs-plugin-mj-vars'
import fontSizePlugin from './plugins/sherpa-grapesjs-plugin-mj-font-size'
import fontBoldPlugin from './plugins/sherpa-grapesjs-plugin-mj-font-bold'
import fontItalicPlugin from './plugins/sherpa-grapesjs-plugin-mj-font-italic'
import fontUnderlinePlugin from './plugins/sherpa-grapesjs-plugin-mj-font-underline'
import fontStrikePlugin from './plugins/sherpa-grapesjs-plugin-mj-font-strike'
import fontLinkPlugin from './plugins/sherpa-grapesjs-plugin-mj-font-link'
import spacerPlugin from './plugins/sherpa-grapesjs-plugin-mj-spacer'
import dividerPlugin from './plugins/sherpa-grapesjs-plugin-mj-divider'
import fontColorPlugin from './plugins/sherpa-grapesjs-plugin-mj-font-color'
import linkOptionsPlugin from './plugins/sherpa-grapesjs-plugin-mj-link-options'
import cssClassPlugin from './plugins/sherpa-grapesjs-plugin-mj-css-class'
import codesPlugin from './plugins/sherpa-grapesjs-plugin-mj-codes'
import assetsManagerPlugin from './plugins/sherpa-grapesjs-plugin-assets-manager'
import assetsManagerExtraFilesPlugin from './plugins/sherpa-grapesjs-plugin-assets-manager-extra-files'

export default {
  name: 'grapes-js-editor',
  props: {
    content: {
      type: Object,
      required: false
    },
    wsEndpoint: {
      type: String,
      required: true
    },
    wsFileuploadProcessor: {
      type: String,
      required: true
    },
    wsGetProcessor: {
      type: String,
      required: true
    },
    lang: {
      type: String,
      required: true
    },
    languageSelected: {
      type: Object,
      required: true
    },
    texts: {
      type: Object,
      required: true
    },
    variables: {
      type: Object,
      required: true
    },
    save: {
      type: Object,
      required: false
    },
    type: {
      type: String,
      required: true
    },
    apiparams: {
      type: Object,
      required: false
    },
  },
  components: {
    SpinnerLoading,
    ImageMap
  },
  data () {
    return {
      loading: true,
      editor: null,
      components: null,
      imgMapValue: {
        uuid: null,
        source: null,
        zones: null,
        width: null,
        height: null
      },
      mapEditorLink: 'map-editor-modal',
      fonts: [
        {name: 'Arial', value: 'Arial', src: null},
        {name: 'Arial Black', value: 'Arial Black', src: null},
        {name: 'Courier', value: 'Courier, monospace', src: null},
        {name: 'Courier Prime', value: 'Courier Prime, Courier, monospace', src: 'https://fonts.googleapis.com/css2?family=Courier+Prime:ital,wght@0,400;0,700;1,400;1,700&display=swap'},
        {name: 'Georgia', value: 'Georgia, Times, Times New Roman, serif', src: this.apiparams.assetsPath + 'static/grapesjs/georgia/georgia.css'},
        {name: 'Lato', value: 'Lato, sans-serif', src: 'https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,100;0,300;0,400;0,700;0,900;1,100;1,300;1,400;1,700;1,900&display=swap'},
        {name: 'Lora', value: 'Lora, sans-serif', src: 'https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&display=swap'},
        {name: 'Montserrat', value: 'Montserrat, sans-serif', src: 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap'},
        {name: 'Open Sans', value: 'Open Sans, sans-serif', src: 'https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300;1,400;1,600;1,700;1,800&display=swap'},
        {name: 'Oswald', value: 'Oswald, sans-serif', src: 'https://fonts.googleapis.com/css2?family=Oswald:wght@200;300;400;469;500;600;700&display=swap'},
        {name: 'Pacifico', value: 'Pacifico, cursive', src: 'https://fonts.googleapis.com/css2?family=Pacifico&display=swap'},
        {name: 'Roboto', value: 'Roboto, sans-serif', src: 'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap'},
        {name: 'sans-serif', value: 'sans-serif', src: null},
        {name: 'Shadows Into Light Two', value: "'Shadows Into Light Two', cursive", src: 'https://fonts.googleapis.com/css2?family=Shadows+Into+Light+Two&display=swap'},
        {name: 'Source Code Pro', value: "'Source Code Pro', sans-serif", src: 'https://fonts.googleapis.com/css2?family=Source+Code+Pro:ital,wght@0,200;0,300;0,400;0,500;0,600;0,700;0,900;1,200;1,300;1,400;1,500;1,600;1,700;1,900&display=swap'},
        {name: 'Tahoma', value: 'Tahoma', src: null},
        {name: 'Times New Roman', value: 'Times New Roman', src: null},
        {name: 'Ubuntu', value: 'Ubuntu', src: 'https://fonts.googleapis.com/css2?family=Ubuntu:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400;1,500;1,700&display=swap'},
        {name: 'Verdana', value: 'Verdana, Geneva, sans-serif', src: null},
      ],
      specialLinkOptions: [
        {value: '', name: ''},
        {value: '*|UNSUB|*', name: 'UNSUB of newsletter'},
        {value: '*|VALIDATE|*', name: 'VALIDATE email'},
        {value: '*|CLICKAQUI|*', name: 'CLICK HERE for see email'},
      ],
      cssStyles: [
        {name: 'no padding', cssName: 'no-padding', value: '.no-padding { padding: 0 !important; }', mobile: true},
        {name: 'no padding top', cssName: 'no-padding-top', value: '.no-padding-top { padding-top: 0 !important; }', mobile: true},
        {name: 'no padding right', cssName: 'no-padding-right', value: '.no-padding-right { padding-right: 0 !important; }', mobile: true},
        {name: 'no padding bottom', cssName: 'no-padding-bottom', value: '.no-padding-bottom { padding-bottom: 0 !important; }', mobile: true},
        {name: 'no padding left', cssName: 'no-padding-left', value: '.no-padding-left { padding-left: 0 !important; }', mobile: true},
        {name: 'text align center', cssName: 'text-align-center', value: '.text-align-center div { text-align: center !important; }', mobile: true},
        {name: 'text align left', cssName: 'text-align-left', value: '.text-align-left div { text-align: left !important; }', mobile: true},
        {name: 'text align right', cssName: 'text-align-right', value: '.text-align-right div { text-align: right !important; }', mobile: true},
        {name: null, cssName: null, value: 'img, area, img[usemap], map area { outline: none !important; }', mobile: false}
        // {name: 'RTL direction', cssName: 'direction-rtl', value: '.direction-rtl > table > tbody > tr > td { -ms-box-orient: horizontal !important; display: -webkit-box !important; display: -moz-box !important; display: -ms-flexbox !important; display: -moz-flex !important; display: -webkit-flex !important; display: flex !important; -webkit-flex-flow: row wrap !important; flex-flow: row wrap !important; }', mobile: true},
        // {name: null, cssName: null, value: '.direction-rtl > table > tbody > tr > td > div:nth-of-type(1) { order: 3 !important; }', mobile: true},
        // {name: null, cssName: null, value: '.direction-rtl > table > tbody > tr > td > div:nth-of-type(2) { order: 2 !important; }', mobile: true},
        // {name: null, cssName: null, value: '.direction-rtl > table > tbody > tr > td > div:nth-of-type(3) { order: 1 !important; }', mobile: true},
      ],
      qrPath: this.apiparams.assetsPath + "static/qr.png",
      barcodePath: this.apiparams.assetsPath + "static/barcode.png",
      initialImagesPagesNumber: 0,
      initialImagesNumber: 0
    }
  },
  methods: {
    changeImgMap (changes) {
      var json = JSON.stringify(changes.zones)
      json = json.replace(/"/gi, '\'')
      this.editor.getSelected().addAttributes({'data-zones': json})
      this.imgMapValue.uuid = null
      this.imgMapValue.source = null
      this.imgMapValue.zones = null
      this.imgMapValue.width = null
      this.imgMapValue.height = null
      this.editor.stopCommand(this.mapEditorLink)
    },
    loadGrapesJS () {
      this.getEditorWithImages()
    },
    getEditorWithImages () {
      var vm = this

      var componentsMjml = ''
      // set innerHtml
      if (this.components && this.components !== '') {

        // refresh mj-head
        var bodyTag = this.components.substr(this.components.indexOf('<mj-body'))
        componentsMjml = '<mjml><mj-head><mj-style>'+ this.getCssStyles() +'</mj-style></mj-head>' + bodyTag

        // remove mj-raw
        componentsMjml = componentsMjml.replace(/<mj-raw>.+<\/mj-raw>/gm, '')

      } else {
        componentsMjml = '<mjml><mj-head><mj-style>'+ this.getCssStyles() +'</mj-style></mj-head><mj-body></mj-body></mjml>'
      }

      this.$refs['grapesjsEditorContainer'].innerHTML = componentsMjml

      // plugins
      var plugins = []
      plugins.push(grapesjsMJML)
      plugins.push(imageMapPlugin)
      plugins.push(exportPlugin)
      plugins.push(fontBoldPlugin)
      plugins.push(fontItalicPlugin)
      plugins.push(fontUnderlinePlugin)
      plugins.push(fontStrikePlugin)
      plugins.push(fontLinkPlugin)
      plugins.push(fontColorPlugin)
      plugins.push(fontSizePlugin)
      plugins.push(varsPlugin)
      plugins.push(spacerPlugin)
      plugins.push(dividerPlugin)
      plugins.push(linkOptionsPlugin)
      plugins.push(cssClassPlugin)
      plugins.push(codesPlugin)
      
      //-- new assets manager from API
      if (this.apiparams.newAssetsManager) {
        plugins.push(assetsManagerPlugin)
      } else {
        plugins.push(tUIMjImageEditor)        
      }

      plugins.push(assetsManagerExtraFilesPlugin)

      var xhr = vm.wsImagesCall(vm, vm.wsGetProcessor, false, (callbackFuncParams) => {
        vm.editor = grapesjs.init({
          fromElement: true,
          container : vm.$refs['grapesjsEditorContainer'],
          avoidInlineStyle : false,
          plugins: plugins,
          pluginsOpts: {
            [grapesjsMJML]: {
              columnsPadding: '5px 0',
              fonts: vm.getMjml2HtmlFonts(),
            },
            [tUIMjImageEditor]: {
              icons: {
                'menu.normalIcon.path': vm.apiparams.assetsPath + 'static/grapesjs/icon-d.svg',
                'menu.activeIcon.path': vm.apiparams.assetsPath + 'static/grapesjs/icon-b.svg',
                'menu.disabledIcon.path': vm.apiparams.assetsPath + 'static/grapesjs/icon-a.svg',
                'menu.hoverIcon.path': vm.apiparams.assetsPath + 'static/grapesjs/icon-c.svg',
                'submenu.normalIcon.path': vm.apiparams.assetsPath + 'static/grapesjs/icon-d.svg',
                'submenu.activeIcon.path': vm.apiparams.assetsPath + 'static/grapesjs/icon-c.svg'
              },
              onApply: (imageEditor, imageModel) => {
                const dataURL = imageEditor.toDataURL()
                var files = []
                files.push(vm.dataUrlToBlob(dataURL, imageEditor.ui.initializeImgUrl))
                vm.uploadImages(files, true)
              },
              addToAssets: false
            },
            [imageMapPlugin]: {
              mapEditorLink: vm.mapEditorLink,
              imgMapValue: vm.imgMapValue
            },
            [varsPlugin]: {
              individual: vm.variables.individual,
              professional: vm.variables.professional,
              indicator: vm.variables.indicator,
              texts: vm.texts
            },
            [linkOptionsPlugin]: {
              specialLinkOptions: vm.specialLinkOptions
            },
            [cssClassPlugin]: {
              cssStyles: vm.cssStyles
            },
            [codesPlugin]: {
              qrPath: vm.qrPath,
              barcodePath: vm.barcodePath,
            },
            [assetsManagerPlugin]: {
              getImagesFunction: vm.wsImagesCall,
              refreshImagesFunction: vm.refreshAssetManager,
              callTypeEndpoint: vm.wsGetProcessor,
              initialImagesPagesNumber: vm.initialImagesPagesNumber,
              initialImagesNumber: vm.initialImagesNumber,
              component: vm,
              loadingImgSrc: vm.apiparams.assetsPath + 'static/grapesjs/loading.gif',
            },
            [assetsManagerExtraFilesPlugin]: {
              'images': {
                'application/pdf': vm.apiparams.htmlAbsolutePath + vm.apiparams.assetsPath + 'static/pdf-image.png'
              },
              isFTP: !this.apiparams.newAssetsManager
            }
          },
          storageManager: {
            type: null, // Disable Storage Manager
            autoload: false
          },
          assetManager: {
            uploadFile: (e) => {
              var files = e.dataTransfer ? e.dataTransfer.files : e.target.files
              vm.uploadImages(files, false)
            },
            upload: false
          },
          colorPicker: {
            appendTo: 'parent',
            offset: { top: 26, left: -166 }
          },
          canvas: {
            styles: vm.getCanvasFontsSrc()
          }
        })

        var head = vm.editor.Canvas.getDocument().head
        head.insertAdjacentHTML('beforeend', '<style>'+ vm.getCssStyles() +'</style>')

        vm.editor.on('load', () => {
          // load asset manager images
          vm.refreshAssetManager(callbackFuncParams)

          // Editor Font List
          var styleManager = vm.editor.StyleManager
          var fontProperty = styleManager.getProperty('typography', 'font-family')
          vm.setEditorFontList(fontProperty)
          styleManager.render()

          vm.loading = false
          vm.$emit('editorLoaded')
        })

        vm.editor.on('component:add', () => { vm.editorEvent('editorChange') })
        vm.editor.on('component:remove', () => { vm.editorEvent('editorChange') })
        vm.editor.on('component:update', () => { vm.editorEvent('editorChange') })

        //-- fix 'padding' style appear when is installed on SherpaCRM
        vm.editor.on('component:selected', (component) => {
          var style = component.getStyle()
          if (style['padding']) {
            var paddingValue = style['padding']
            delete style['padding']
            var paddingValues = paddingValue.split(/[ ]+/);
            if (!style['padding-top']) {
              style['padding-top'] = paddingValues[0]
            }
            if (!style['padding-right']) {
              style['padding-right'] = paddingValues[1]
            }
            if (!style['padding-bottom']) {
              style['padding-bottom'] = paddingValues[2]
            }
            if (!style['padding-left']) {
              style['padding-left'] = paddingValues[3]
            }
            component.setStyle(style)
          }
        })

        //-- add body padding-bottom
        vm.editor.CssComposer.setRule('.body-padding', { 'padding-bottom': '20px' })
        vm.editor.getWrapper().findType('mj-body')[0].addClass('body-padding')

        var needRefresh = false

        //-- fix 'columnizable' add refresh
        vm.editor.on('component:add', (component) => {
          // to prevent 'columnizable' default
        	switch(component.get('type')) {
        	case 'mj-navbar-link':
        	case 'mj-social-element':
        	case 'mj-column':
        		needRefresh = true
        		break;
        	default:
        		//nothing
        	}
        })

        vm.editor.on('update', () => {
        	// to prevent 'columnizable' elements render default
          if (needRefresh) {
            vm.editor.setComponents(vm.editor.getHtml())
        		needRefresh = false
        	}
        })
      })

      if (xhr) {
        //-- first search data
        xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8')
        xhr.send(JSON.stringify({"search" : "", "page" : 1}))
      }
    },
    uploadImages (files, refreshAndClose) {
      var xhr = this.wsImagesCall(this, this.wsFileuploadProcessor, refreshAndClose)

      if (xhr) {
        var formData = new FormData()
        for (var i = 0; i < files.length; i++) {
          formData.append('file-' + i, files[i], files[i].name) // containing all the selected images from local
        }
        xhr.send(formData)
      }
    },
    wsImagesCall (vm, callTypeEndpoint, refreshAndClose, callback, callbackParam) {
      var xhr = null

      var multimediaUrl = this.wsEndpoint + callTypeEndpoint

      if (multimediaUrl && multimediaUrl !== '') {
        xhr = new XMLHttpRequest()
        xhr.withCredentials = false
        xhr.open("POST", multimediaUrl)
        xhr.onload = () => {
          var data
          if (xhr.status !== 200) {
            console.error('HTTP Error: ' + xhr.status)
            return
          }
          data = JSON.parse(xhr.responseText)
          if (!data) {
            console.error('Invalid JSON: ' + xhr.responseText)
          } else {
            vm.initialImagesPagesNumber = data.pages
            vm.initialImagesNumber = data.total

            if (callback) {
              var param = null

              if (callbackParam) {
                param = callbackParam
              } else {
                param = {}
              }

              param.files = data.files
              param.refreshAndClose = refreshAndClose
              param.newImagesPagesNumber = data.pages
              param.newImagesNumber = data.total

              callback(param)
            } else {
              vm.refreshAssetManager({ "files" : data.files, "refreshAndClose" : refreshAndClose })
            }
          }
        }
      }

      return xhr
    },
    refreshAssetManager (params) {
      var files = params.files
      var refreshAndClose = params.refreshAndClose

      var vm = this

      for (var i = 0; i < files.length; i++) {
        var file = files[i]

        // get image dimensions by service or client
        if (file.contentType && file.contentType.indexOf('image') > -1 && file.width === 0 && file.height === 0) {
          var imageAux = document.createElement('img')
          imageAux.setAttribute('src', file.thumbnailUrl)
          imageAux.image = file
          imageAux.onload = function () {
            this.image.width = this.naturalWidth
            this.image.height = this.naturalHeight
            vm.addAssetFile(refreshAndClose, this.image)
          }
        } else {
          this.addAssetFile(refreshAndClose, file)
        }
      }      
    },
    addAssetFile (refreshAndClose, file) {
      if (file.type.indexOf('image') > -1) {
        this.editor.AssetManager.add({
          src: file.thumbnailUrl,
          type: file.type,
          width: file.width,
          height: file.height,
          name: file.name,
        })
      } else if (file.type.indexOf('pdf') > -1) {
        this.editor.AssetManager.add({
          type: file.type,
          src: file.url,
          pdfName: file.name,
          thumbnail: file.thumbnailUrl      
        })
      }      
      if (refreshAndClose) {
        this.editor.getSelected().set({ src: file.thumbnailUrl })
      }
    },
    dataUrlToBlob (dataURL, initializeImgUrl) {
      const data = dataURL.split(',')
      const byteStr = window.atob(data[1])
      const type = data[0].split(':')[1].split(';')[0]
      const nameArray = initializeImgUrl.split('/')
      const name = nameArray[nameArray.length - 1]
      const ab = new ArrayBuffer(byteStr.length)
      const ia = new Uint8Array(ab)

      for (let i = 0; i < byteStr.length; i++) {
        ia[i] = byteStr.charCodeAt(i)
      }

      var blob = new Blob([ab], { type })
      blob.name = name
      return blob
    },
    editorEvent (eventName) {
      var template = {
        components: this.editor.getHtml()
      }
      if (template.components && template.components !== '') {
        var html = this.editor.runCommand('mjml-get-code').html

        // Replace QR / CODIGOBARRAS params
        html = html.replaceAll('src="'+ this.qrPath +'"', 'src="*|QR|*"')
        html = html.replaceAll('src="'+ this.barcodePath +'"', 'src="*|BARCODE|*"')

        // Replace &gt; on RTL direction styles
        html = html.replaceAll('.direction-rtl &gt; table &gt; tbody &gt; tr &gt; td &gt; ', '.direction-rtl > table > tbody > tr > td > ')

        this.$emit(eventName, JSON.stringify(template), html)
      }
    },
    getCanvasFontsSrc () {
      var result = []
      for (var i = 0; i < this.fonts.length; i++) {
        if (this.fonts[i].src) {
          result.push(this.fonts[i].src)
        }
      }
      return result
    },
    setEditorFontList (fontProperty) {
      var list = []
      // empty list
      fontProperty.set('list', list);

      // custom list
      for (var i = 0; i < this.fonts.length; i++) {
        list.push(fontProperty.addOption({value: this.fonts[i].value, name: this.fonts[i].name}))
      }
      fontProperty.set('list', list)
    },
    getMjml2HtmlFonts () {
      var result = {}
      for (var i = 0; i < this.fonts.length; i++) {
        if (this.fonts[i].src) {
          result[this.fonts[i].name] = this.fonts[i].src
        }
      }
      return result
    },
    getCssStyles () {
      var resultMobile = '\n@media only screen and (max-width:480px) {\n '
      var result = '\n'

      if (this.cssStyles) {
        for (var i = 0; i < this.cssStyles.length; i++) {
          if (this.cssStyles[i].mobile) {
            resultMobile += '\n' + this.cssStyles[i].value + ' ' 
          } else {
            result += '\n' + this.cssStyles[i].value + ' '
          }
        }
      }
      resultMobile += '\n}'

      return result + ' ' + resultMobile
    }
  },
  beforeMount () {
    if (this.content) {
      if (this.content.components) {
        this.components = this.content.components
      }
    }
  },
  mounted () {
    this.loadGrapesJS()
  },
  watch: {
    save (save) {
      this.editorEvent('editorSave')
    },
    imgMapValue: {
      handler (val) {
        if (!val.uuid) {
          this.editor.stopCommand(this.mapEditorLink)
        }
      },
      deep: true
    }
  }
}
</script>

<style lang="scss">
.gjs-field option {
  background-color: #222;
}
.gjs-clm-tags {
  display: none !important;
}
.CodeMirror-lines div {
  text-align: left;
}
.gjs-am-meta {
  line-break: anywhere;
}
.gjs-new-asset-manager {
  .gjs-am-assets-cont {
    width: 100%;
    float: left;
    height: inherit;
    padding: 10px 10px 5px 10px;

    .gjs-am-assets-header {
      padding: 5px 0 5px 5px;
      position: relative;
      text-align: left;

      .gjs-searcher-input {
        width: 75%;
        background-color: #5F5F5F;
        border: 0;
        padding: 5px;
        font-family: Helvetica,sans-serif;
        font-size: 13px;
        color: #ddd;
      }

      .gjs-original-label {
        margin: 0 6px 0 40px;
        vertical-align: middle;
        font-size: 12px;
      }

      .gjs-original-input {
        width: 16px;
        height: 16px;
        vertical-align: middle;
      }

      i {
        position: absolute;
        color: #ddd;
        padding: 4px 5px;
        right: 3px;
        top: 8px;
        border-radius: 4px;
        background-color: rgba(255,255,255,0.3);
        cursor: pointer;
        transition: all 0.1s ease-in;

        &:hover {
          background-color: rgba(255,255,255,0.5);
        }
      }
    }

    .gjs-am-assets {
      height: 470px;

      .gjs-am-asset {
        width: 50%;

        .gjs-am-preview-cont {
          height: 80px;
          min-height: 80px;
        }

        .gjs-am-preview-cont,
        .gjs-am-meta {
          width: 50%;
        }

        .gjs-am-close {
          display: none;
        }
      }
    }

    .gjs-am-assets-footer {
      padding: 10px 0 5px 5px;

      button {
        color: #ddd;
        margin: 0px 2px;
        border-radius: 4px;
        background-color: rgba(255,255,255,0.3);
        cursor: pointer;
        transition: all 0.1s ease-in;
        min-width: 24px;
        border: 0;
        padding: 3px 0;
        font-size: 12px;

        &:hover,
        &.selected {
          background-color: rgba(255,255,255,0.5);
        }
      }

      .total {
        font-size: 12px;
      }
    }

    .loading {
      position: absolute;
      opacity: 0;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      background-color: rgba(60,60,60,0.8);
      transition: all 0.1s ease-in;
      z-index: -1;

      &.active {
        opacity: 1;
        z-index: 1;
      }

      img {
        position: absolute;
        width: 72px;
        top: calc(50% - 36px);
      }
    }

  }
}
</style>



// WEBPACK FOOTER //
// node_modules/vue-grapesjs-editor/src/components/GrapesJsMjmlEditor.vue